import { CancelAppointmentContent } from "@/constants/EditEvents";
import type { EditAppointmentScope } from "@/constants/EditEvents";
import { AppointmentCancellationReason } from "@/constants/Schedule";
import { ConfirmationAlertTestID, TextTestID } from "@/constants/TestID";
import { useGetCancellationSubReasonsQuery } from "@/store/api/schedule/schedule";
import BottomSheetModal from "@/ui/composed/modal/BottomSheetModal";
import MultiSelectTextField from "@/ui/composed/pickers/MultiSelectTextField";
import { Button, FAIconName, Text } from "@/ui/core";
import { HStack, VStack } from "@/ui/layout";
import { joinTestIDs } from "@/utils/string.util";

interface CancelAppointmentModalProps extends ViewWithTestID {
  cancellationReason?: MultiSelectOption;
  editAppointmentScope?: EditAppointmentScope;
  isOpen: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
  setCancellationReason: (reason?: MultiSelectOption) => void;
}

const CancelAppointmentModal = ({
  cancellationReason,
  editAppointmentScope = "appointment",
  isOpen,
  onConfirm,
  onDismiss,
  setCancellationReason,
  testID,
}: CancelAppointmentModalProps) => {
  const baseTestID = joinTestIDs(testID, ConfirmationAlertTestID.Primary);

  const {
    data: cancellationReasonsData,
    isLoading: isCancellationReasonsLoading,
  } = useGetCancellationSubReasonsQuery(AppointmentCancellationReason.User);

  const cancellationReasonOptions =
    cancellationReasonsData?.data.map((reason) => ({
      displayText: reason.subReason,
      value: reason.code,
    })) || [];

  const config = CancelAppointmentContent[editAppointmentScope];

  return (
    <BottomSheetModal
      contentContainerGap={12}
      dismissAccessibilityLabel="Dismiss Confirmation Alert"
      isOpen={isOpen}
      onRequestClose={onDismiss}
      testID={baseTestID}
      title={config.title}
    >
      <VStack gap={18}>
        <Text
          testID={joinTestIDs(baseTestID, TextTestID.Description)}
          variant="body"
        >
          {config.description}
        </Text>
        <Text
          testID={joinTestIDs(baseTestID, TextTestID.Caption)}
          variant="body"
        >
          {config.caption}
        </Text>
        <MultiSelectTextField
          isDisabled={isCancellationReasonsLoading}
          isSingleSelect
          modalTitle="Reason for cancellation"
          onValuesChange={(selection) => {
            setCancellationReason(
              selection.length > 0 ? selection[0] : undefined,
            );
          }}
          options={cancellationReasonOptions}
          testID={joinTestIDs(baseTestID, "reason-for-cancellation")}
          value={cancellationReason?.displayText}
        />
        <HStack justifyContent="space-between">
          <Button
            leftIcon={FAIconName.ChevronLeft}
            onPress={onDismiss}
            testID={joinTestIDs(baseTestID, "keep-appointment")}
            title="Reschedule instead"
            variant="tertiary"
          />
          <Button
            isDisabled={!cancellationReason}
            leftIcon={FAIconName.Trash}
            onPress={onConfirm}
            testID={joinTestIDs(baseTestID, "confirm")}
            title="Cancel"
            variant="accent.critical"
          />
        </HStack>
      </VStack>
    </BottomSheetModal>
  );
};

export type { CancelAppointmentModalProps };
export default CancelAppointmentModal;
